/**
 * WebSocket service for real-time chat
 * Handles connection, reconnection, and message handling
 */

export type WSMessageType =
  | 'new_message'
  | 'message_deleted'
  | 'typing_start'
  | 'typing_stop'
  | 'presence_update'
  | 'message_read'
  | 'notification'
  | 'invalidate'
  | 'error'
  | 'pong';

export interface WSMessage {
  type: WSMessageType;
  data: any;
  // Present on 'invalidate' messages: which API entity changed (e.g. "factures")
  // and what happened to it. Used to refetch just that entity's cached queries.
  entity?: string;
  action?: 'create' | 'update' | 'delete';
}

export type MessageHandler = (message: WSMessage) => void;

class ChatWebSocketService {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10; // Increased for better resilience
  private reconnectDelay = 2000; // Start with 2 seconds
  private pingInterval: NodeJS.Timeout | null = null;
  private messageHandlers: Set<MessageHandler> = new Set();
  private isConnecting = false;
  private token: string | null = null;
  private tokenRefreshCallback: (() => Promise<string>) | null = null;

  /**
   * Connect to WebSocket server
   */
  connect(token: string): void {
    if (this.isConnecting || (this.ws && this.ws.readyState === WebSocket.OPEN)) {
      return;
    }

    this.token = token;
    this.isConnecting = true;

    // Remove trailing slash from API URL and convert http to ws
    const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(/\/$/, '').replace('http', 'ws');
    const wsUrl = `${baseUrl}/api/chat/ws/chat`;

    try {
      // Pass the JWT via the WebSocket subprotocol handshake (RFC 6455
      // `Sec-WebSocket-Protocol`) instead of the URL query string. The browser
      // sends this as an HTTP header during the upgrade — it is NOT logged in
      // access logs, browser history, or Referer headers (OWASP ZAP fix).
      this.ws = new WebSocket(wsUrl, ['jwt', token]);

      this.ws.onopen = () => {
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.startPing();
      };

      this.ws.onmessage = (event) => {
        try {
          const message: WSMessage = JSON.parse(event.data);
          this.handleMessage(message);
        } catch (error) {
          // Silent fail for parse errors
        }
      };

      this.ws.onclose = () => {
        this.isConnecting = false;
        this.stopPing();
        this.attemptReconnect();
      };

      this.ws.onerror = () => {
        this.isConnecting = false;
      };
    } catch (error) {
      this.isConnecting = false;
    }
  }

  /**
   * Set a callback to refresh the token when needed
   */
  setTokenRefreshCallback(callback: () => Promise<string>): void {
    this.tokenRefreshCallback = callback;
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    this.stopPing();
    this.reconnectAttempts = this.maxReconnectAttempts; // Prevent reconnection
    if (this.ws) {
      this.ws.close(1000, 'User disconnected');
      this.ws = null;
    }
    this.token = null;
    this.tokenRefreshCallback = null;
  }

  /**
   * Send a message through WebSocket
   */
  send(type: string, data: any): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type, ...data }));
    }
  }

  /**
   * Send typing indicator
   */
  sendTyping(conversationId: number, isTyping: boolean): void {
    this.send(isTyping ? 'typing_start' : 'typing_stop', { conversation_id: conversationId });
  }

  /**
   * Mark messages as read
   */
  markRead(conversationId: number, messageId?: number): void {
    this.send('mark_read', { conversation_id: conversationId, message_id: messageId });
  }

  /**
   * Enter a conversation (for tracking who's viewing)
   */
  enterConversation(conversationId: number): void {
    this.send('enter_conversation', { conversation_id: conversationId });
  }

  /**
   * Leave a conversation
   */
  leaveConversation(conversationId: number): void {
    this.send('leave_conversation', { conversation_id: conversationId });
  }

  /**
   * Subscribe to messages
   */
  onMessage(handler: MessageHandler): () => void {
    this.messageHandlers.add(handler);
    return () => this.messageHandlers.delete(handler);
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  private handleMessage(message: WSMessage): void {
    this.messageHandlers.forEach((handler) => {
      try {
        handler(message);
      } catch (error) {
        // Silent fail for handler errors
      }
    });
  }

  private async attemptReconnect(): Promise<void> {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      return;
    }

    this.reconnectAttempts++;
    // Cap the delay at 30 seconds
    const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1), 30000);

    setTimeout(async () => {
      try {
        // Try to get a fresh token if callback is available
        if (this.tokenRefreshCallback) {
          const newToken = await this.tokenRefreshCallback();
          this.token = newToken;
        }

        if (this.token) {
          this.connect(this.token);
        }
      } catch (error) {
        // Try again with existing token
        if (this.token) {
          this.connect(this.token);
        }
      }
    }, delay);
  }

  private startPing(): void {
    this.stopPing();
    this.pingInterval = setInterval(() => {
      this.send('ping', {});
    }, 30000);
  }

  private stopPing(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }
}

// Singleton instance
export const chatWebSocket = new ChatWebSocketService();
export default chatWebSocket;
